import type { Dispatch, SetStateAction } from 'react'

import { Button } from '@runtime/components/ui/Button'
import type { I18nT } from '@runtime/core/i18n/i18n'
import styles from '@runtime/features/profile/history/review/HistoryReplayControls.module.scss'

type HistoryReplayControlsProps = {
  t: I18nT
  movesCount: number
  reviewIndex: number
  reviewPlaying: boolean
  setReviewIndex: Dispatch<SetStateAction<number>>
  setReviewPlaying: Dispatch<SetStateAction<boolean>>
}

export default function HistoryReplayControls({
  t,
  movesCount,
  reviewIndex,
  reviewPlaying,
  setReviewIndex,
  setReviewPlaying,
}: HistoryReplayControlsProps) {
  if (movesCount <= 0) return null

  return (
    <div className={styles.reviewPlayer}>
      <div className={styles.reviewPlayerRow}>
        <div className={styles.reviewPlayerButtons}>
          <Button
            type="button"
            size="s"
            className={styles.reviewPlayerButton}
            onClick={() => {
              setReviewPlaying(false)
              setReviewIndex(0)
            }}
            disabled={reviewIndex === 0}
            title={t('profile.review.buttons.first')}
          >
            ⏮
          </Button>
          <Button
            type="button"
            size="s"
            className={styles.reviewPlayerButton}
            onClick={() => {
              setReviewPlaying(false)
              setReviewIndex((index) => Math.max(0, index - 1))
            }}
            disabled={reviewIndex === 0}
            title={t('profile.review.buttons.prev')}
          >
            ◀
          </Button>
          <Button
            type="button"
            size="s"
            className={styles.reviewPlayerButton}
            onClick={() => setReviewPlaying((playing) => !playing)}
            title={
              reviewPlaying
                ? t('profile.review.buttons.pause')
                : t('profile.review.buttons.play')
            }
          >
            {reviewPlaying ? '⏸' : '▶'}
          </Button>
          <Button
            type="button"
            size="s"
            className={styles.reviewPlayerButton}
            onClick={() => {
              setReviewPlaying(false)
              setReviewIndex((index) => Math.min(movesCount, index + 1))
            }}
            disabled={reviewIndex >= movesCount}
            title={t('profile.review.buttons.next')}
          >
            ▶
          </Button>
          <Button
            type="button"
            size="s"
            className={styles.reviewPlayerButton}
            onClick={() => {
              setReviewPlaying(false)
              setReviewIndex(movesCount)
            }}
            disabled={reviewIndex >= movesCount}
            title={t('profile.review.buttons.last')}
          >
            ⏭
          </Button>
        </div>

        <div className={styles.reviewPlayerStatus}>
          {reviewIndex === 0
            ? t('profile.review.statusStart')
            : t('profile.review.statusMove', {
                index: reviewIndex,
                count: movesCount,
              })}
        </div>
      </div>

      <div className={styles.reviewPlayerSlider}>
        <input
          type="range"
          min="0"
          max={movesCount}
          value={reviewIndex}
          onChange={(event) => {
            setReviewPlaying(false)
            setReviewIndex(Number(event.target.value) || 0)
          }}
        />
      </div>
    </div>
  )
}
